import { useTheme } from '@/components/common/ThemeProvider';
import { CustomerLocationDTO } from '@/shared/api/model';
import {
  showSuccessNotification,
  showErrorNotification
} from '@/components/common/utils/notify';
import { useQueryClient } from '@tanstack/react-query';
import { 
  useGetCustomerLocations, 
  useGetCustomerLocationsByType,
  useSaveCustomerLocation as useOlvalSaveCustomerLocation,
  useDeleteCustomerLocation as useOlvalDeleteCustomerLocation,
  getGetCustomerLocationsQueryKey,
  getGetCustomerLocationsByTypeQueryKey
} from '@/shared/api/client/customer-locations';

export const useCustomerLocation = () => {
  const queryClient = useQueryClient();
  const { dark } = useTheme();

  // Query hooks
  const useCustomerLocations = (options?: { enabled?: boolean }) => {
    const result = useGetCustomerLocations({
      query: {
        enabled: options?.enabled !== undefined ? options.enabled : true
      }
    });
    
    if (result.error) {
      showErrorNotification('Error', `Failed to fetch locations: ${(result.error as any)?.message || 'Unknown error'}`, dark);
    }
    
    return result;
  };

  const useCustomerLocationsByType = (addressType: string) => {
    const result = useGetCustomerLocationsByType(addressType, {
      query: {
        enabled: !!addressType
      }
    });
    
    if (result.error) {
      showErrorNotification('Error', `Failed to fetch ${addressType} location: ${(result.error as any)?.message || 'Unknown error'}`, dark);
    }
    
    return result;
  };

  // Mutation hooks
  const useSaveCustomerLocation = () => {
    return useOlvalSaveCustomerLocation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetCustomerLocationsQueryKey() });
          showSuccessNotification('Success', 'Location saved successfully', dark);
        },
        onError: (error) => {
          showErrorNotification('Error', `Failed to save location: ${(error as any)?.message || 'Unknown error'}`, dark);
        }
      }
    });
  };

  const useDeleteCustomerLocation = () => {
    return useOlvalDeleteCustomerLocation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetCustomerLocationsQueryKey() });
          showSuccessNotification('Success', 'Location deleted successfully', dark);
        },
        onError: (error) => {
          showErrorNotification('Error', `Failed to delete location: ${(error as any)?.message || 'Unknown error'}`, dark);
        }
      }
    });
  };

  return {
    useCustomerLocations,
    useCustomerLocationsByType,
    useSaveCustomerLocation,
    useDeleteCustomerLocation
  };
};

export default useCustomerLocation;
